import { Tooltip } from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { EnvelopeIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCurrentSize } from "../CurrentSize";
import { useQuery } from "../AppLayout";
import TaskAction from "../actions/TaskAction";
import ConfirmDialog from "./ConfirmDialog";
import { useTranslation } from "react-i18next";
import ActivityAction from "../actions/ActivityAction";
import TemplateAction from "../actions/TemplateAction";
import { ActivityTypes, TemplateContexts } from "../constants/action-types";

const SelectionItemsDialog = ({ onBulkDelete }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentParams = useQuery();
  const currentSize = useCurrentSize();
  const selectionIds = useSelector((state: any) => state.taskReducer.getIn(["selectionIds"]));
  const isOpen = selectionIds.size > 0;
  const [showDeletion, setShowDeletion] = useState(false);

  const handleDelete = () => {
    dispatch(
      TaskAction.bulkDeleteItems({
        itemIds: selectionIds.toJS(),
        type: currentParams.get("type"),
      })
    ).then(() => {
      onBulkDelete();
      setShowDeletion(false);
      dispatch(
        TaskAction.selectAllItems({
          view: "GRID",
          selected: false,
        })
      );
    });
  };

  const showSendEmail = () => {
    dispatch(
      ActivityAction.showActivityDialog(null, null, {
        itemIds: selectionIds.toJS().join(","),
        sendBulkEmail: true,
        attendees: [],
        activityType: ActivityTypes.sendEmail,
      })
    );
  };

  useEffect(() => {
    dispatch(
      TaskAction.selectAllItems({
        view: "GRID",
        selected: false,
      })
    );
    if (currentParams.get("type")) {
      dispatch(
        TemplateAction.getAttributes([
          currentParams.get("type"),
          TemplateContexts.SENDER,
          TemplateContexts.RECIPIENT,
        ])
      );
    }
  }, [currentParams, dispatch]);

  const showEmailAction =
    currentParams.get("type") === "CONTACT" || currentParams.get("type") === "COMPANY";

  const renderDeletionDescription = () => {
    return (
      <div className={"space-y-4"}>
        <div>
          {t(
            "You’re about to permanently delete these {{size}} items. All associated activities will be deleted.",
            { size: selectionIds.size }
          )}
        </div>

        <div>{t("If you’re not sure, you can resolve or close these items instead.")}</div>
      </div>
    );
  };
  return (
    <>
      <Tooltip
        variant={"light"}
        offset={0}
        clickable
        opacity={60}
        className={"crm-tooltip"}
        place={"bottom"}
        position={{
          x: currentSize.width / 2,
          y: currentSize.height - 40,
        }}
        isOpen={isOpen}
      >
        <div className="flex items-center rounded-md shadow-xl">
          <div className="flex bg-white/5 p-2 items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="rounded flex items-center bg-blue-600 h-6 p-2 text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                {selectionIds.size}
              </div>

              <div className="text-sm">{t("selected")}</div>
            </div>
            {showEmailAction && (
              <div
                className="flex items-center cursor-pointer gap-x-1 h-9 rounded-xl shadow border border-gray-200 px-2 text-gray-900 hover:bg-gray-100"
                onClick={showSendEmail}
              >
                <EnvelopeIcon className={"w-3"} />
                <span className="text-gray-900">Send emails</span>
              </div>
            )}
            <div
              className="flex items-center cursor-pointer gap-x-1 h-9 rounded-xl shadow border border-gray-200 px-2 text-gray-900 hover:text-red-400 hover:bg-gray-100"
              onClick={() => {
                setShowDeletion(true);
              }}
            >
              <TrashIcon className={"w-3"} />
              <span className="">{t("Delete records")}</span>
            </div>
            <XMarkIcon
              className={
                "w-6 cursor-pointer hover:bg-blue-500 hover:text-white rounded-md p-1 justify-end"
              }
              onClick={() => {
                dispatch(
                  TaskAction.selectAllItems({
                    view: "GRID",
                    selected: false,
                  })
                );
              }}
            />
          </div>
        </div>
      </Tooltip>
      <ConfirmDialog
        visible={showDeletion}
        description={renderDeletionDescription()}
        title={t("Delete {{size}} items?", { size: selectionIds.size })}
        onClose={() => {
          setShowDeletion(false);
        }}
        onSubmit={handleDelete}
      />
    </>
  );
};
export default SelectionItemsDialog;
